import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, ChevronLeft, Send, Smartphone, X } from 'lucide-react';

import { Button, Card } from '@llura/ui';

import { ApiError, api } from '../../lib/api-client';
import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import { usePushSubscription } from '../../hooks/usePushSubscription';
import { useNotificationPrefsStore } from '../../stores/notification-prefs.store';
import { useRitualStore } from '../../stores/ritual.store';
import { isIos, isStandalone } from '../../lib/platform';
import { CadencePicker, type CadenceValue } from '../ritual/cadence-picker';

const DEFAULT_REMINDER_TIME = '20:00';

type TestState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'done'; ok: boolean; message: string };

/**
 * Dedicated Notifications settings screen at `/profile/notifications`. Bundles
 * the reminder toggle + time, the ritual-cadence frequency picker (which drives
 * how often the worker nudges), a live status panel, a self-test button, and a
 * link to the install tutorial.
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings } = useStrings();
  const t = strings.notifications.settings;

  const { reminderEnabled, reminderTime, setReminderEnabled, setReminderTime } =
    useNotificationPrefsStore();
  const { subscribe, permission, isSubscribed, unsupported } = usePushSubscription();

  const ritual = useRitualStore();
  const cadence: CadenceValue = {
    count: ritual.count,
    period: ritual.period,
    intensity: ritual.intensity,
  };

  const [test, setTest] = useState<TestState>({ kind: 'idle' });

  const toggleReminders = async (next: boolean) => {
    haptic.tap();
    if (next) {
      // Make sure we have a user-linked subscription before enabling. subscribe()
      // requests permission; it's a no-op if already granted.
      await subscribe();
    }
    setReminderEnabled(next);
  };

  const onCadenceChange = (next: CadenceValue) => {
    // The store needs a concrete count+period; fill sensible defaults for the
    // rare partial pick (e.g. a count tapped before any period was chosen).
    ritual.setCadence({
      count: next.count ?? 1,
      period: next.period ?? 'week',
      intensity: next.intensity,
    });
  };

  const sendTest = async () => {
    haptic.tap();
    setTest({ kind: 'sending' });
    try {
      const { sent } = await api.notifications.testSelf();
      setTest(
        sent > 0
          ? { kind: 'done', ok: true, message: t.testSuccess }
          : { kind: 'done', ok: false, message: t.testNoDevice },
      );
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 429 ? t.testCooldown : t.testError;
      setTest({ kind: 'done', ok: false, message });
    }
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5">
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
          aria-label={t.backAria}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-serif text-2xl leading-tight">{t.title}</h1>
          <p className="text-xs text-muted-foreground">{t.intro}</p>
        </div>
      </header>

      {/* Reminders master toggle + time */}
      <Card className="space-y-4 border-border bg-card p-5">
        <h2 className="font-serif text-lg leading-tight">{strings.notifications.prefs.title}</h2>

        <label className="flex items-center justify-between gap-3 text-sm">
          <span>{strings.notifications.prefs.enableLabel}</span>
          <input
            type="checkbox"
            checked={reminderEnabled}
            onChange={(e) => void toggleReminders(e.target.checked)}
            className="h-5 w-5 accent-primary"
          />
        </label>

        {reminderEnabled && (
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>{strings.notifications.prefs.timeLabel}</span>
            <input
              type="time"
              value={reminderTime ?? DEFAULT_REMINDER_TIME}
              onChange={(e) => setReminderTime(e.target.value)}
              className="rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        )}
      </Card>

      {/* Frequency — reuses the ritual cadence picker */}
      <Card className="space-y-4 border-border bg-card p-5">
        <div className="space-y-1">
          <h2 className="font-serif text-lg leading-tight">{t.frequencyTitle}</h2>
          <p className="text-xs text-muted-foreground">{t.frequencyHint}</p>
        </div>
        <CadencePicker value={cadence} onChange={onCadenceChange} />
      </Card>

      {/* Status & test */}
      <Card className="space-y-4 border-border bg-card p-5">
        <h2 className="font-serif text-lg leading-tight">{t.statusTitle}</h2>

        <div className="space-y-2">
          {unsupported ? (
            <StatusRow ok={false} label={t.status.unsupported} />
          ) : (
            <>
              <StatusRow
                ok={permission === 'granted'}
                label={permission === 'denied' ? t.status.blocked : t.status.allowed}
              />
              <StatusRow
                ok={isSubscribed}
                label={isSubscribed ? t.status.subscribed : t.status.notSubscribed}
              />
              {isIos() && (
                <StatusRow
                  ok={isStandalone()}
                  label={isStandalone() ? t.status.installed : t.status.needsInstall}
                />
              )}
            </>
          )}
        </div>

        <div className="space-y-2">
          <Button
            onClick={sendTest}
            disabled={!isSubscribed || test.kind === 'sending'}
            className="w-full gap-2 rounded-xl"
          >
            <Send className="h-4 w-4" />
            {test.kind === 'sending' ? t.testSending : t.testCta}
          </Button>
          {test.kind === 'done' && (
            <p
              className={
                test.ok
                  ? 'text-xs text-primary'
                  : 'text-xs text-muted-foreground'
              }
            >
              {test.message}
            </p>
          )}
        </div>
      </Card>

      {/* Install */}
      <Card className="space-y-3 border-border bg-card p-5">
        <div className="space-y-1">
          <h2 className="font-serif text-lg leading-tight">{t.installTitle}</h2>
          <p className="text-xs text-muted-foreground">{t.installHint}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/profile/install')}
          className="w-full justify-start gap-2 rounded-xl border-border"
        >
          <Smartphone className="h-4 w-4" />
          {t.installCta}
        </Button>
      </Card>
    </div>
  );
}

function StatusRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <Check className="h-4 w-4 shrink-0 text-primary" />
      ) : (
        <X className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className={ok ? 'text-foreground/90' : 'text-muted-foreground'}>{label}</span>
    </div>
  );
}
